import TR from 'utils/TR';
import useRect from 'hooks/common/useRect';
import React from 'react';
import T from 'utils/T';
import ex from 'utils/ex';

import PeriodCalendarTitle from './PeriodCalendarTitle';
import PeriodSetButton from './PeriodSetButton';
import LinearCalendar from 'components/common/LinearCalendar';

import * as Luxon from 'luxon';
import VBox from 'components/common/VBox';

import './PeriodCalendar.css';
import PeriodOffsetButton from 'components/header/period/PeriodOffsetButton';
import { luxonClone } from 'utils/time';
import useForceUpdate from 'hooks/common/useForceUpdate';
import hotkey from 'utils/hotkey';
import useLayoutRect from 'hooks/common/useLayoutRect';

export default function PeriodCalendar(props) {
  const {
    id,
    reference, // Element
    placement,
    connection, // 'start-arrow' (default) | 'line' | 'end-arrow' | 'arrow' | 'none'
    width, // integer | 'auto'
    height, // integer | 'auto'
    veilable, // Boolean
    stickable, // Boolean
    movable, // Boolean
    closable, // Boolean
    escapable, // Boolean
    modal, // Boolean
    dim, // color (default=transparent)
    tags = [],
    type,
    title,
    description,
    contents,
    buttons = [],
    events = {},
    props: {
      luxon_start,
      luxon_end,
      date_availables,
    },
    close,
    ...rest
  } = props || {};

  const today = React.useMemo(() => Luxon.DateTime.now(), []);

  const ref = React.useRef();
  const rect = useLayoutRect(reference);
  const floatingRect = useRect(ref);

  const style = React.useMemo(() => {
    const info = {};
    switch (placement) {
      case 'top-left': {
        break;
      }
      case 'top': {
        break;
      }
      case 'top-right': {
        break;
      }
      case 'bottom-left': {
        break;
      }
      case 'bottom': {
        break;
      }
      case 'bottom-right': {
        const top = rect.top + rect.height;
        const right = window.innerWidth - (rect.left + rect.width);
        info.top = top;
        info.right = right;
        break;
      }
      case 'left-top': {
        break;
      }
      case 'left': {
        break;
      }
      case 'left-bottom': {
        break;
      }
      case 'right-top': {
        break;
      }
      case 'right': {
        break;
      }
      case 'right-bottom': {
        break;
      }
      default: {
        break;
      }
    }
    return info;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placement, rect.height, rect.left, rect.top, rect.width, floatingRect.top, floatingRect.left]);

  const [selection_start, setSelectionStart] = React.useState(null);
  const [selection_end, setSelectionEnd] = React.useState(null);
  const [selection, setSelection] = React.useState(null);

  const select = React.useCallback((date, end) => {
    if (date && end) {
      setSelection(() => null);
      setSelectionStart(() => date);
      setSelectionEnd(() => end);
    } else if (!selection) {
      setSelection(() => date);
      setSelectionStart(() => null);
      setSelectionEnd(() => null);
    } else {
      if (selection.toMillis() < date.toMillis()) {
        setSelectionStart(() => selection);
        setSelectionEnd(() => date);
        setSelection(() => null);
      } else {
        setSelectionStart(() => date);
        setSelectionEnd(() => selection);
        setSelection(() => null);
      }
    }
  }, [selection]);

  const forceUpdate = useForceUpdate();
  const basisRef = React.useRef();
  const onSelect = React.useCallback((date, end) => (e) => {
    basisRef.current = null;
    select(date, end);
  }, [select]);

  const onToday = React.useCallback((days) => (e) => {
    basisRef.current = today;
    setSelectionStart(() => null);
    setSelectionEnd(() => null);
    setSelection(() => null);
    forceUpdate();
  }, [forceUpdate, today]);

  const onTrigger = React.useCallback((days) => (e) => {
    basisRef.current = null;
    const date = days < 0 ? selection.minus({ days: Math.abs(days) }) : selection.plus({ days: Math.abs(days) });
    select(date);
  }, [select, selection]);

  React.useEffect(() => {
    const cancel = hotkey(['Escape']);
    const onKeyDown = (e) => {
      cancel(e, (e) => {
        if (!selection) return;
        e.preventDefault();
        e.stopPropagation();
        setSelection(() => null);
      });
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [selection]);

  return (
    <div
      {...ex('Dim')}
      {...{ onClick: events?.onOuterClick(ref) }}
    >
      <div
        {...ex('PeriodCalendar', rest)}
        {...{
          ref,
          style,
        }}
      >
        <VBox>
          <PeriodCalendarTitle {...{ onClose: events?.onClose }} />
          <LinearCalendar
            {...{
              date_availables,
              selection_start,
              selection_end,
              selection,
              onSelect,
              max: today,
              basis: selection_end || luxon_end || today,
              forceBasis: basisRef.current,
              onPeriodChanged: events?.onPeriodChanged,
            }}
          />
          <PeriodSetButton
            {...{
              selection_start,
              selection_end,
              onClick: events?.onSubmit(selection_start, selection_end),
            }}
          />
        </VBox>
        {!selection && luxon_end && (luxon_end.year !== today.year || luxonClone(luxon_end).weekNumber !== today.weekNumber) && (
          <VBox {...ex('Left')}>
            <PeriodOffsetButton {...{ days: 0, label: T('오늘'), selection: today, onTrigger: onToday }} />
          </VBox>
        )}
        {selection && (
          <VBox {...ex('Left')}>
            <PeriodOffsetButton {...{ days: -90, label: '-90', selection, onTrigger }} />
            <PeriodOffsetButton {...{ days: -30, label: '-30', selection, onTrigger }} />
            <PeriodOffsetButton {...{ days: -14, label: '-14', selection, onTrigger }} />
          </VBox>
        )}
        {selection && (
          <VBox {...ex('Right')}>
            <PeriodOffsetButton {...{ days: 14, label: '+14', selection, onTrigger }} />
            <PeriodOffsetButton {...{ days: 30, label: '+30', selection, onTrigger }} />
            <PeriodOffsetButton {...{ days: 90, label: '+90', selection, onTrigger }} />
          </VBox>
        )}
      </div>
    </div>
  );
}
